import React from 'react';
import { Listbox, ListboxOption, ListboxLabel } from '../listbox/Listbox.component';

export interface IMultiSelectOption {
    value: string | number;
    label: string;
    group?: string | null;
}

export interface MultiSelectListboxProps {
    options: IMultiSelectOption[];
    value: Array<string | number>;
    onChange: (values: Array<string | number>) => void;
    placeholder?: string;
    className?: string;
    disabled?: boolean;
    ariaLabel?: string;
}

export const MultiSelectListbox: React.FunctionComponent<MultiSelectListboxProps> = (props: MultiSelectListboxProps): JSX.Element => {
    const { options, value, onChange, placeholder, className, disabled, ariaLabel } = props;

    const valueSet = React.useMemo(() => new Set(value.map((v) => String(v))), [value]);
    const selectedOptions = React.useMemo(() => options.filter((o) => valueSet.has(String(o.value))), [options, valueSet]);

    // Bucket options by group. Options without an explicit `group` collect
    // into a synthetic "Ungrouped" bucket — but we render that bucket flat
    // (no header, no wrapper div) so the Listbox trigger only renders the
    // matching option content, not a sibling group-header label.
    const grouped = React.useMemo(() => {
        const map = new Map<string, IMultiSelectOption[]>();
        for (const opt of options) {
            const key = (opt.group || 'Ungrouped') as string;
            if (!map.has(key)) map.set(key, []);
            (map.get(key) as IMultiSelectOption[]).push(opt);
        }
        return Array.from(map.entries());
    }, [options]);

    const hasRealGroups = React.useMemo(
        () => options.some((o) => o.group && o.group.trim().length > 0),
        [options]
    );

    return (
        <Listbox
            multiple
            value={selectedOptions}
            onChange={(selected: IMultiSelectOption[] | IMultiSelectOption) => {
                const arr = Array.isArray(selected) ? selected : [selected];
                onChange(arr.map((o) => o.value));
            }}
            className={className}
            placeholder={placeholder || 'Select...'}
            aria-label={ariaLabel || 'Multi Select'}
            disabled={disabled}
        >
            {hasRealGroups
                ? grouped.map(([groupName, groupOptions]) => (
                    <div key={groupName} className="px-1 py-1">
                        {groupName !== 'Ungrouped' && (
                            <div className="px-2 py-1 text-xs font-semibold text-zinc-500 dark:text-zinc-400">{groupName}</div>
                        )}
                        {groupOptions.map((opt) => (
                            <ListboxOption key={String(opt.value)} value={opt}>
                                <ListboxLabel>{opt.label}</ListboxLabel>
                            </ListboxOption>
                        ))}
                    </div>
                ))
                : options.map((opt) => (
                    <ListboxOption key={String(opt.value)} value={opt}>
                        <ListboxLabel>{opt.label}</ListboxLabel>
                    </ListboxOption>
                ))}
        </Listbox>
    );
}


